import { create } from 'zustand';
import { getDb, exportBackupData, importBackupData, MallaBackupData } from '../db';
import { Asignatura, Evaluacion, Material, EscalaCalificacion, CarpetaNota, NotaGeneral, HorarioClase, EventoCalendario, RegistroHistorial, Facultad, Carrera, Modulo, Ciclo, AcreditacionLibre } from '../logic/types';
import { DEFAULT_ESCALA_FCEA, DEFAULT_ESCALA_PSICO, resolverConcepto, resolverCalificacionItem } from '../logic/calculos';
import { generarMallaTEA } from '../data/seedTecnicatura';

export interface NavigationTarget {
  view: 'dashboard' | 'roadmap' | 'asignaturas' | 'notas' | 'config' | 'horarios';
  asignaturaId?: string;
  category?: string;
}

interface AppState {
  facultades: Facultad[];
  carreras: Carrera[];
  activeFacultadId: string;
  activeCarreraId: string | null;
  asignaturas: Asignatura[];
  evaluaciones: Evaluacion[];
  materiales: Material[];
  escalas: EscalaCalificacion[];
  carpetasNotas: CarpetaNota[];
  notasGenerales: NotaGeneral[];
  horarios: HorarioClase[];
  eventosCalendario: EventoCalendario[];
  historialAcademico: RegistroHistorial[];
  modulos: Modulo[];
  ciclos: Ciclo[];
  acreditacionesLibres: AcreditacionLibre[];
  isLoading: boolean;
  navigationTarget: NavigationTarget | null;

  // Acciones de Facultad & Carrera
  setActiveFacultad: (facultadId: string) => void;
  setActiveCarrera: (carreraId: string | null) => void;
  setModoVacioConfirmado: (carreraId: string, vacio: boolean) => Promise<void>;

  // Estado de Simulación y Demo Mode
  isSimulating: boolean;
  simulationChanges: number;
  realAsignaturasBackup: Asignatura[] | null;
  startSimulation: () => void;
  stopSimulation: () => void;
  resetSimulation: () => void;

  isDemoMode: boolean;
  demoBackup: { asignaturas: Asignatura[]; evaluaciones: Evaluacion[]; activeFacultadId: string; activeCarreraId: string | null } | null;
  startDemoMode: () => void;
  stopDemoMode: () => void;

  // Acciones
  navigateTo: (target: NavigationTarget) => void;
  clearNavigationTarget: () => void;
  loadData: () => Promise<void>;
  addAsignatura: (asignatura: Asignatura) => Promise<void>;
  addAsignaturas: (asignaturas: Asignatura[]) => Promise<void>;
  updateAsignatura: (id: string, asignatura: Partial<Asignatura>) => Promise<void>;
  deleteAsignatura: (id: string) => Promise<void>;
  hardDeleteAsignatura: (id: string) => Promise<void>;
  addEvaluacion: (evaluacion: Evaluacion) => Promise<void>;
  updateEvaluacion: (id: string, updates: Partial<Evaluacion>) => Promise<void>;
  deleteEvaluacion: (id: string) => Promise<void>;
  updateEscala: (escala: EscalaCalificacion) => Promise<void>;
  addMaterial: (material: Material) => Promise<void>;
  deleteMaterial: (id: string) => Promise<void>;
  addCarpetaNota: (carpeta: CarpetaNota) => Promise<void>;
  updateCarpetaNota: (id: string, updates: Partial<CarpetaNota>) => Promise<void>;
  deleteCarpetaNota: (id: string) => Promise<void>;
  addNota: (nota: NotaGeneral) => Promise<void>;
  updateNota: (id: string, updates: Partial<NotaGeneral>) => Promise<void>;
  deleteNota: (id: string) => Promise<void>;
  hardDeleteNota: (id: string) => Promise<void>;

  addHorario: (horario: HorarioClase) => Promise<void>;
  updateHorario: (id: string, updates: Partial<HorarioClase>) => Promise<void>;
  deleteHorario: (id: string) => Promise<void>;

  addEventoCalendario: (evento: EventoCalendario) => Promise<void>;
  updateEventoCalendario: (id: string, updates: Partial<EventoCalendario>) => Promise<void>;
  deleteEventoCalendario: (id: string) => Promise<void>;

  addRegistroHistorial: (registro: RegistroHistorial) => Promise<void>;
  updateRegistroHistorial: (id: string, updates: Partial<RegistroHistorial>) => Promise<void>;
  deleteRegistroHistorial: (id: string) => Promise<void>;

  addAcreditacionLibre: (acreditacion: AcreditacionLibre) => Promise<void>;
  deleteAcreditacionLibre: (id: string) => Promise<void>;

  exportBackupJson: () => Promise<MallaBackupData>;
  importBackupJson: (backupData: MallaBackupData) => Promise<{ success: boolean; importedCount: number }>;
}

export const useStore = create<AppState>((set, get) => ({
  facultades: [],
  carreras: [],
  activeFacultadId: localStorage.getItem('malla_active_facultad_id') || 'fcea',
  activeCarreraId: localStorage.getItem('malla_active_carrera_id') || null,
  asignaturas: [],
  evaluaciones: [],
  materiales: [],
  escalas: [DEFAULT_ESCALA_FCEA, DEFAULT_ESCALA_PSICO],
  carpetasNotas: [],
  notasGenerales: [],
  horarios: [],
  eventosCalendario: [],
  historialAcademico: [],
  modulos: [],
  ciclos: [],
  acreditacionesLibres: [],
  isLoading: true,
  navigationTarget: null,

  setActiveFacultad: (facultadId: string) => {
    localStorage.setItem('malla_active_facultad_id', facultadId);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-faculty', facultadId);
    }
    set({ activeFacultadId: facultadId });
  },

  setActiveCarrera: (carreraId: string | null) => {
    if (carreraId) {
      localStorage.setItem('malla_active_carrera_id', carreraId);
    } else {
      localStorage.removeItem('malla_active_carrera_id');
    }
    set({ activeCarreraId: carreraId });
  },

  setModoVacioConfirmado: async (carreraId: string, vacio: boolean) => {
    const db = await getDb();
    await db.execute('UPDATE carreras SET modo_vacio_confirmado = $1 WHERE id = $2', [vacio ? 1 : 0, carreraId]);
    set(state => ({
      carreras: state.carreras.map(c => c.id === carreraId ? { ...c, modo_vacio_confirmado: vacio } : c)
    }));
  },

  isSimulating: false,
  simulationChanges: 0,
  realAsignaturasBackup: null,

  startSimulation: () => {
    const current = get().asignaturas;
    set({
      isSimulating: true,
      simulationChanges: 0,
      realAsignaturasBackup: JSON.parse(JSON.stringify(current))
    });
  },

  resetSimulation: () => {
    const backup = get().realAsignaturasBackup;
    if (backup) {
      set({
        asignaturas: JSON.parse(JSON.stringify(backup)),
        simulationChanges: 0
      });
    }
  },

  stopSimulation: () => {
    const backup = get().realAsignaturasBackup;
    if (backup) {
      set({
        asignaturas: JSON.parse(JSON.stringify(backup)),
        isSimulating: false,
        simulationChanges: 0,
        realAsignaturasBackup: null
      });
    } else {
      set({
        isSimulating: false,
        simulationChanges: 0
      });
    }
  },

  isDemoMode: false,
  demoBackup: null,

  startDemoMode: () => {
    const state = get();
    const backup = {
      asignaturas: [...state.asignaturas],
      evaluaciones: [...state.evaluaciones],
      activeFacultadId: state.activeFacultadId,
      activeCarreraId: state.activeCarreraId,
    };

    // Generar malla demo poblada con avance de ejemplo
    const sampleAsignaturas = generarMallaTEA('unificado').map(a => {
      if (a.id === 'A10' || a.id === 'C10' || a.id === 'E10') {
        return { ...a, estado: 'aprobada' as const, nota_final: 9, concepto_final: 'Muy Bueno' };
      }
      if (a.id === 'M10' || a.id === 'L10') {
        return { ...a, estado: 'en_curso' as const };
      }
      return a;
    });

    set({
      isDemoMode: true,
      demoBackup: backup,
      activeFacultadId: 'fcea',
      activeCarreraId: 'carrera_fcea_tea',
      asignaturas: sampleAsignaturas,
      navigationTarget: { view: 'roadmap' }
    });

    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-faculty', 'fcea');
    }
  },

  stopDemoMode: () => {
    const backup = get().demoBackup;
    if (backup) {
      set({
        isDemoMode: false,
        demoBackup: null,
        asignaturas: backup.asignaturas,
        evaluaciones: backup.evaluaciones,
        activeFacultadId: backup.activeFacultadId,
        activeCarreraId: backup.activeCarreraId,
      });
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-faculty', backup.activeFacultadId);
      }
    } else {
      set({ isDemoMode: false });
    }
  },

  navigateTo: (target: NavigationTarget) => {
    set({ navigationTarget: target });
  },

  clearNavigationTarget: () => {
    set({ navigationTarget: null });
  },

  loadData: async () => {
    try {
      set({ isLoading: true });
      const db = await getDb();
      
      // 0. Cargar Facultades y Carreras
      let facultadesRaw = await db.select<any[]>('SELECT * FROM facultades');
      if (facultadesRaw.length === 0) {
        const facultadesInit = [
          { id: 'fcea', nombre: 'Facultad de Ciencias Económicas y de Administración', sigla: 'FCEA', disponible: 1, escala_calificacion_id: 'escala_fcea_2025', terminologia: JSON.stringify({ materia: 'Asignatura', previa: 'Previatura' }), design_tokens_ref: 'design-fcea.md' },
          { id: 'psico', nombre: 'Facultad de Psicología', sigla: 'PSICO', disponible: 1, escala_calificacion_id: 'escala_psico_default', terminologia: JSON.stringify({ materia: 'UCO', previa: 'Previatura' }), design_tokens_ref: 'design-psico.md' }
        ];
        for (const f of facultadesInit) {
          await db.execute(
            'INSERT OR IGNORE INTO facultades (id, nombre, sigla, disponible, escala_calificacion_id, terminologia, design_tokens_ref) VALUES ($1, $2, $3, $4, $5, $6, $7)',
            [f.id, f.nombre, f.sigla, f.disponible, f.escala_calificacion_id, f.terminologia, f.design_tokens_ref]
          );
        }
        facultadesRaw = await db.select<any[]>('SELECT * FROM facultades');
      }

      const facultades: Facultad[] = facultadesRaw.map(f => ({
        ...f,
        disponible: Boolean(f.disponible),
        terminologia: typeof f.terminologia === 'string' ? JSON.parse(f.terminologia) : f.terminologia
      }));

      let carrerasRaw = await db.select<any[]>('SELECT * FROM carreras');
      if (carrerasRaw.length === 0) {
        const carrerasInit = [
          { id: 'carrera_fcea_tea', facultad_id: 'fcea', nombre: 'Tecnicatura en Administración', tipo: 'tecnicatura', creditos_totales: 225, duracion_semestres: 5, locacion: 'Montevideo', malla_precargada: 1 },
          { id: 'carrera_fcea_cp', facultad_id: 'fcea', nombre: 'Contador Público', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: 0 },
          { id: 'carrera_fcea_la', facultad_id: 'fcea', nombre: 'Licenciatura en Administración', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: 0 },
          { id: 'carrera_fcea_le', facultad_id: 'fcea', nombre: 'Licenciatura en Economía', tipo: 'licenciatura', creditos_totales: 360, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: 0 },
          { id: 'carrera_psico_lic', facultad_id: 'psico', nombre: 'Licenciatura en Psicología', tipo: 'licenciatura', creditos_totales: 320, duracion_semestres: 8, locacion: 'Montevideo', malla_precargada: 1 }
        ];
        for (const c of carrerasInit) {
          await db.execute(
            'INSERT OR IGNORE INTO carreras (id, facultad_id, nombre, tipo, creditos_totales, duracion_semestres, locacion, malla_precargada) VALUES ($1, $2, $3, $4, $5, $6, $7, $8)',
            [c.id, c.facultad_id, c.nombre, c.tipo, c.creditos_totales, c.duracion_semestres, c.locacion, c.malla_precargada]
          );
        }
        carrerasRaw = await db.select<any[]>('SELECT * FROM carreras');
      }

      const carreras: Carrera[] = carrerasRaw.map(c => ({
        ...c,
        malla_precargada: Boolean(c.malla_precargada),
        modo_vacio_confirmado: Boolean(c.modo_vacio_confirmado)
      }));

      const currentActiveFac = get().activeFacultadId || 'fcea';
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-faculty', currentActiveFac);
      }

      // 1. Cargar escalas de calificación
      let escalasRaw = await db.select<any[]>('SELECT * FROM escalas_calificacion');
      let escalas: EscalaCalificacion[] = [];
      if (escalasRaw.length === 0) {
        await db.execute(
          `INSERT OR REPLACE INTO escalas_calificacion (id, facultad, nombre, niveles) VALUES ($1, $2, $3, $4)`,
          [DEFAULT_ESCALA_FCEA.id, DEFAULT_ESCALA_FCEA.facultad, DEFAULT_ESCALA_FCEA.nombre, JSON.stringify(DEFAULT_ESCALA_FCEA.niveles)]
        );
        await db.execute(
          `INSERT OR REPLACE INTO escalas_calificacion (id, facultad, nombre, niveles) VALUES ($1, $2, $3, $4)`,
          [DEFAULT_ESCALA_PSICO.id, DEFAULT_ESCALA_PSICO.facultad, DEFAULT_ESCALA_PSICO.nombre, JSON.stringify(DEFAULT_ESCALA_PSICO.niveles)]
        );
        escalas = [DEFAULT_ESCALA_FCEA, DEFAULT_ESCALA_PSICO];
      } else {
        escalas = escalasRaw.map(e => ({
          ...e,
          niveles: typeof e.niveles === 'string' ? JSON.parse(e.niveles) : e.niveles
        }));
      }

      const escalaFCEA = escalas.find(e => e.facultad === 'FCEA') || DEFAULT_ESCALA_FCEA;

      // 2. Traer asignaturas, evaluaciones, materiales, carpetas, notas, horarios y eventos de calendario
      const asignaturasRaw = await db.select<any[]>('SELECT * FROM asignaturas');
      const evaluacionesRaw = await db.select<any[]>('SELECT * FROM evaluaciones');
      const materiales = await db.select<Material[]>('SELECT * FROM materiales');
      const carpetasNotas = await db.select<CarpetaNota[]>('SELECT * FROM carpetas_notas');
      const notasRaw = await db.select<any[]>('SELECT * FROM notas_generales');
      const horarios = await db.select<HorarioClase[]>('SELECT * FROM horarios_clases');
      const eventosCalendario = await db.select<EventoCalendario[]>('SELECT * FROM eventos_calendario ORDER BY fecha ASC');
      const historialAcademico = await db.select<RegistroHistorial[]>('SELECT * FROM historial_academico');

      let modulos = await db.select<Modulo[]>('SELECT * FROM modulos');
      if (modulos.length === 0) {
        const modulosDefault: Modulo[] = [
          { id: 'mod_fcea_admin', facultad_id: 'fcea', nombre: 'Área Administración (A)', creditos_minimos: 60, color_hex: '#EA514D' },
          { id: 'mod_fcea_conta', facultad_id: 'fcea', nombre: 'Área Contabilidad e Impuestos (C)', creditos_minimos: 50, color_hex: '#0284C7' },
          { id: 'mod_fcea_metcant', facultad_id: 'fcea', nombre: 'Área Métodos Cuantitativos (MC)', creditos_minimos: 30, color_hex: '#436461' },
          { id: 'mod_fcea_juri', facultad_id: 'fcea', nombre: 'Área Jurídica (J)', creditos_minimos: 20, color_hex: '#6366F1' },
          { id: 'mod_fcea_integradoras', facultad_id: 'fcea', nombre: 'Área Actividades Integradoras (I)', creditos_minimos: 15, color_hex: '#10B981' },
          { id: 'mod_fcea_econ', facultad_id: 'fcea', nombre: 'Área Economía (E)', creditos_minimos: 10, color_hex: '#F59E0B' },
          { id: 'mod_fcea_cdf', facultad_id: 'fcea', nombre: 'Área Formativos / Optativos (CDF)', creditos_minimos: 30, color_hex: '#8B5CF6' },
          { id: 'mod_psicologico', facultad_id: 'psico', nombre: 'Módulo Psicológico', creditos_minimos: 140, color_hex: '#0099A9' },
          { id: 'mod_metodologico', facultad_id: 'psico', nombre: 'Módulo Metodológico', creditos_minimos: 60, color_hex: '#0284C7' },
          { id: 'mod_articulacion', facultad_id: 'psico', nombre: 'Módulo Articulación de Saberes', creditos_minimos: 50, color_hex: '#0D9488' },
          { id: 'mod_practicas', facultad_id: 'psico', nombre: 'Módulo Prácticas y Proyectos', creditos_minimos: 70, color_hex: '#10B981' }
        ];
        for (const m of modulosDefault) {
          await db.execute(
            'INSERT OR IGNORE INTO modulos (id, facultad_id, nombre, creditos_minimos, color_hex) VALUES ($1, $2, $3, $4, $5)',
            [m.id, m.facultad_id, m.nombre, m.creditos_minimos, m.color_hex || null]
          );
        }
        modulos = modulosDefault;
      }

      let ciclos = await db.select<Ciclo[]>('SELECT * FROM ciclos ORDER BY orden ASC');
      if (ciclos.length === 0) {
        const ciclosDefault: Ciclo[] = [
          { id: 'ciclo_inicial', facultad_id: 'psico', nombre: 'Ciclo Inicial', creditos_minimos: 80, orden: 1 },
          { id: 'ciclo_formacion', facultad_id: 'psico', nombre: 'Ciclo Formativo Integral', creditos_minimos: 160, orden: 2 },
          { id: 'ciclo_graduacion', facultad_id: 'psico', nombre: 'Ciclo de Graduación', creditos_minimos: 80, orden: 3 }
        ];
        for (const c of ciclosDefault) {
          await db.execute(
            'INSERT OR IGNORE INTO ciclos (id, facultad_id, nombre, creditos_minimos, orden) VALUES ($1, $2, $3, $4, $5)',
            [c.id, c.facultad_id, c.nombre, c.creditos_minimos, c.orden]
          );
        }
        ciclos = ciclosDefault;
      }

      const acreditacionesLibres = await db.select<AcreditacionLibre[]>('SELECT * FROM acreditaciones_libres');

      const asignaturas: Asignatura[] = asignaturasRaw.map(a => {
        const califResuelta = resolverCalificacionItem(a, escalaFCEA);
        return {
          ...a,
          requisitos: a.requisitos ? JSON.parse(a.requisitos) : [],
          porcentaje_logro: a.porcentaje_logro ?? (a.nota_final !== undefined && a.nota_final !== null ? (a.nota_final / 12) * 100 : undefined),
          concepto_final: a.concepto_final ?? califResuelta?.concepto,
          en_papelera: Boolean(a.en_papelera),
          es_opcional: Boolean(a.es_opcional),
          obligatoria: !Boolean(a.es_opcional),
          dictado: a.dictado || undefined,
          previas_sugeridas: a.previas_sugeridas ? (typeof a.previas_sugeridas === 'string' ? JSON.parse(a.previas_sugeridas) : a.previas_sugeridas) : undefined,
          calificacion_oficial: a.calificacion_oficial !== undefined ? a.calificacion_oficial : null,
          aplazos: a.aplazos ? (typeof a.aplazos === 'string' ? JSON.parse(a.aplazos) : a.aplazos) : []
        };
      });

      const evaluaciones: Evaluacion[] = evaluacionesRaw.map(e => {
        const califResuelta = resolverCalificacionItem(e, escalaFCEA);
        return {
          ...e,
          porcentaje_logro: e.porcentaje_logro ?? (e.nota !== undefined && e.nota !== null ? (e.escala === '0-100' ? e.nota : (e.nota / 12) * 100) : undefined),
          concepto: e.concepto ?? califResuelta?.concepto
        };
      });

      const notasGenerales: NotaGeneral[] = notasRaw.map(n => ({
        ...n,
        tags: n.tags ? (typeof n.tags === 'string' ? JSON.parse(n.tags) : n.tags) : [],
        en_papelera: Boolean(n.en_papelera)
      }));

      // Si no existen notas, sembrar una nota inicial de bienvenida
      if (notasGenerales.length === 0) {
        const notaBienvenida: NotaGeneral = {
          id: 'nota_bienvenida',
          titulo: 'Bienvenido a tus Apuntes en Malla',
          contenido: JSON.stringify([
            { id: '1', type: 'heading', props: { level: 1 }, content: [{ type: 'text', text: 'Apuntes y Notas de Estudio' }] },
            { id: '2', type: 'paragraph', content: [{ type: 'text', text: 'Este editor interactivo te permite tomar notas enriquecidas con formato BlockNote, organizarlas en carpetas libres o vincularlas a tus asignaturas/UCOs.' }] },
            { id: '3', type: 'heading', props: { level: 2 }, content: [{ type: 'text', text: 'Funcionalidades Destacadas' }] },
            { id: '4', type: 'bulletListItem', content: [{ type: 'text', text: 'Auto-guardado automático con debounce en tu base de datos SQLite.' }] },
            { id: '5', type: 'bulletListItem', content: [{ type: 'text', text: 'Creación de carpetas y subcarpetas ilimitadas.' }] },
            { id: '6', type: 'bulletListItem', content: [{ type: 'text', text: 'Buscador de texto completo sobre títulos y contenidos.' }] }
          ]),
          tags: ['Bienvenida', 'Apuntes'],
          fecha_creacion: new Date().toISOString(),
          fecha_modificacion: new Date().toISOString()
        };

        await db.execute(
          `INSERT OR REPLACE INTO notas_generales (id, titulo, contenido, carpeta_id, asignatura_id, evento_id, tags, en_papelera, fecha_creacion, fecha_modificacion) 
           VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)`,
          [
            notaBienvenida.id, notaBienvenida.titulo, notaBienvenida.contenido,
            notaBienvenida.carpeta_id || null, notaBienvenida.asignatura_id || null, notaBienvenida.evento_id || null,
            JSON.stringify(notaBienvenida.tags || []), notaBienvenida.en_papelera ? 1 : 0,
            notaBienvenida.fecha_creacion, notaBienvenida.fecha_modificacion
          ]
        );
        notasGenerales.push(notaBienvenida);
      }

      // Auto-alineación de facultad y carrera activa según datos persistidos o asignaturas presentes
      const storedActiveFac = localStorage.getItem('malla_active_facultad_id') || get().activeFacultadId || 'fcea';
      let finalActiveFac = storedActiveFac;

      if (asignaturas.length > 0) {
        const fceaCount = asignaturas.filter(a => !a.en_papelera && (a.facultad_origen === 'fcea' || (a.plan_id && (a.plan_id.includes('fcea') || a.plan_id.includes('tea'))))).length;
        const psicoCount = asignaturas.filter(a => !a.en_papelera && (a.facultad_origen === 'psico' || (a.plan_id && a.plan_id.includes('psico')))).length;

        if (fceaCount > 0 && psicoCount === 0 && storedActiveFac !== 'fcea') {
          finalActiveFac = 'fcea';
          localStorage.setItem('malla_active_facultad_id', 'fcea');
        } else if (psicoCount > 0 && fceaCount === 0 && storedActiveFac !== 'psico') {
          finalActiveFac = 'psico';
          localStorage.setItem('malla_active_facultad_id', 'psico');
        }
      }

      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-faculty', finalActiveFac);
      }

      // Resolver la carrera activa para la facultad seleccionada
      let storedActiveCarrera = localStorage.getItem('malla_active_carrera_id') || get().activeCarreraId;
      if (!storedActiveCarrera || !carreras.some(c => c.id === storedActiveCarrera && c.facultad_id === finalActiveFac)) {
        if (finalActiveFac === 'fcea') {
          storedActiveCarrera = 'carrera_fcea_tea';
          localStorage.setItem('malla_active_carrera_id', 'carrera_fcea_tea');
        } else if (finalActiveFac === 'psico') {
          storedActiveCarrera = 'carrera_psico_lic';
          localStorage.setItem('malla_active_carrera_id', 'carrera_psico_lic');
        }
      }

      // Migración legacy desde localStorage hacia la base de datos sincronizada
      const legacyModoVacio = localStorage.getItem('malla_modo_vacio');
      if (legacyModoVacio && storedActiveCarrera) {
        const isVacio = legacyModoVacio === 'true';
        await db.execute('UPDATE carreras SET modo_vacio_confirmado = $1 WHERE id = $2', [isVacio ? 1 : 0, storedActiveCarrera]);
        localStorage.removeItem('malla_modo_vacio');
        const targetC = carreras.find(c => c.id === storedActiveCarrera);
        if (targetC) targetC.modo_vacio_confirmado = isVacio;
      }

      set({ 
        facultades, 
        carreras, 
        activeFacultadId: finalActiveFac,
        activeCarreraId: storedActiveCarrera,
        asignaturas, 
        evaluaciones, 
        materiales, 
        escalas, 
        carpetasNotas, 
        notasGenerales, 
        horarios, 
        eventosCalendario, 
        historialAcademico, 
        modulos, 
        ciclos, 
        acreditacionesLibres, 
        isLoading: false 
      });
    } catch (error) {
      console.error('Error loading data:', error);
      set({ isLoading: false });
    }
  },

  exportBackupJson: async (): Promise<MallaBackupData> => {
    return await exportBackupData();
  },

  importBackupJson: async (backupData: MallaBackupData): Promise<{ success: boolean; importedCount: number }> => {
    const res = await importBackupData(backupData);
    await get().loadData();
    return res;
  },

  addAcreditacionLibre: async (acreditacion: AcreditacionLibre) => {
    try {
      const db = await getDb();
      await db.execute(
        'INSERT INTO acreditaciones_libres (id, usuario_id, facultad_id, modulo_id, nombre_actividad, creditos, fecha_acreditacion) VALUES ($1, $2, $3, $4, $5, $6, $7)',
        [
          acreditacion.id,
          acreditacion.usuario_id || 'invitado',
          acreditacion.facultad_id,
          acreditacion.modulo_id || null,
          acreditacion.nombre_actividad,
          acreditacion.creditos,
          acreditacion.fecha_acreditacion
        ]
      );
      const current = get().acreditacionesLibres;
      set({ acreditacionesLibres: [...current, acreditacion] });
    } catch (err) {
      console.error('Error al agregar acreditación libre:', err);
    }
  },

  deleteAcreditacionLibre: async (id: string) => {
    try {
      const db = await getDb();
      await db.execute('DELETE FROM acreditaciones_libres WHERE id = $1', [id]);
      const current = get().acreditacionesLibres;
      set({ acreditacionesLibres: current.filter(a => a.id !== id) });
    } catch (err) {
      console.error('Error al eliminar acreditación libre:', err);
    }
  },


  addAsignatura: async (asignatura: Asignatura) => {
    try {
      const db = await getDb();
      const escalas = get().escalas;
      const escalaFCEA = escalas.find(e => e.facultad === 'FCEA') || DEFAULT_ESCALA_FCEA;
      const calif = resolverCalificacionItem(asignatura, escalaFCEA);

      const porcentaje_logro = asignatura.porcentaje_logro ?? (calif ? calif.porcentaje_min : undefined);
      const concepto_final = asignatura.concepto_final ?? calif?.concepto;

      const es_opcional = asignatura.es_opcional !== undefined ? Boolean(asignatura.es_opcional) : (asignatura.obligatoria !== undefined ? !asignatura.obligatoria : false);
      const newAsig = { ...asignatura, porcentaje_logro, concepto_final, en_papelera: Boolean(asignatura.en_papelera), es_opcional, obligatoria: !es_opcional, calificacion_oficial: asignatura.calificacion_oficial ?? null, aplazos: asignatura.aplazos ?? [] };

      if (newAsig.plan_id) {
        await db.execute(
          `INSERT OR IGNORE INTO planes_estudio (id, nombre, facultad, duracion_semestres) VALUES ($1, $2, $3, $4)`,
          [newAsig.plan_id, 'Plan de Estudios', newAsig.facultad_origen?.toUpperCase() || 'FCEA', 8]
        );
      }

      await db.execute(
        `INSERT INTO asignaturas (id, plan_id, nombre, codigo, semestre_sugerido, creditos, departamento, facultad_origen, estado, requisitos, periodo_cursado, fecha_inicio, fecha_fin, fecha_aprobacion, ano_cursado, nota_final, porcentaje_logro, concepto_final, posicion_x, posicion_y, eva_url, sala9_url, en_papelera, es_opcional, dictado, previas_sugeridas, calificacion_oficial, aplazos) 
         VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18, $19, $20, $21, $22, $23, $24, $25, $26, $27, $28)
         ON CONFLICT(id) DO UPDATE SET
           nombre = excluded.nombre,
           codigo = excluded.codigo,
           semestre_sugerido = excluded.semestre_sugerido,
           creditos = excluded.creditos,
           departamento = excluded.departamento,
           requisitos = excluded.requisitos,
           es_opcional = excluded.es_opcional,
           dictado = excluded.dictado,
           previas_sugeridas = excluded.previas_sugeridas,
           calificacion_oficial = excluded.calificacion_oficial`,
        [
          newAsig.id, newAsig.plan_id, newAsig.nombre, newAsig.codigo || null, 
          newAsig.semestre_sugerido || null, newAsig.creditos, newAsig.departamento || null, 
          newAsig.facultad_origen || null, newAsig.estado, 
          newAsig.requisitos ? JSON.stringify(newAsig.requisitos) : null,
          newAsig.periodo_cursado || null, newAsig.fecha_inicio || null, newAsig.fecha_fin || null,
          newAsig.fecha_aprobacion || null, newAsig.ano_cursado || null, newAsig.nota_final || null,
          newAsig.porcentaje_logro ?? null, newAsig.concepto_final || null,
          newAsig.posicion_x ?? null, newAsig.posicion_y ?? null,
          newAsig.eva_url || null, newAsig.sala9_url || null,
          newAsig.en_papelera ? 1 : 0,
          newAsig.es_opcional ? 1 : 0,
          newAsig.dictado || null,
          newAsig.previas_sugeridas ? JSON.stringify(newAsig.previas_sugeridas) : null,
          newAsig.calificacion_oficial,
          JSON.stringify(newAsig.aplazos)
        ]
      );
      
      set(state => {
        const map = new Map(state.asignaturas.map(a => [a.id, a]));
        const existente = map.get(newAsig.id);
        if (existente) {
          map.set(newAsig.id, {
            ...existente,
            nombre: newAsig.nombre,
            codigo: newAsig.codigo,
            semestre_sugerido: newAsig.semestre_sugerido,
            creditos: newAsig.creditos,
            departamento: newAsig.departamento,
            requisitos: newAsig.requisitos,
            es_opcional: newAsig.es_opcional,
            obligatoria: newAsig.obligatoria,
            dictado: newAsig.dictado,
            previas_sugeridas: newAsig.previas_sugeridas,
            calificacion_oficial: newAsig.calificacion_oficial
          });
        } else {
          map.set(newAsig.id, newAsig);
        }
        return { asignaturas: Array.from(map.values()) };
      });
    } catch (error) {
      console.error('Error adding/updating asignatura:', error);
    }
  },

  addAsignaturas: async (asignaturas: Asignatura[]) => {
    try {
      const db = await getDb();
      const escalas = get().escalas;
      const escalaFCEA = escalas.find(e => e.facultad === 'FCEA') || DEFAULT_ESCALA_FCEA;

      const preparadas: Asignatura[] = [];
      const batchStatements: Array<{ query: string; params: any[] }> = [];

      const planIds = new Set<string>();
      for (const a of asignaturas) {
        if (a.plan_id && !planIds.has(a.plan_id)) {
          planIds.add(a.plan_id);
          batchStatements.push({
            query: `INSERT OR IGNORE INTO planes_estudio (id, nombre, facultad, duracion_semestres) VALUES ($1, $2, $3, $4)`,
            params: [a.plan_id, 'Plan de Estudios', a.facultad_origen?.toUpperCase() || 'FCEA', 8]
          });
        }
      }

      for (const asignatura of asignaturas) {
        const calif = resolverCalificacionItem(asignatura, escalaFCEA);
        const porcentaje_logro = asignatura.porcentaje_logro ?? (calif ? calif.porcentaje_min : undefined);
        const concepto_final = asignatura.concepto_final ?? calif?.concepto;
        const es_opcional = asignatura.es_opcional !== undefined ? Boolean(asignatura.es_opcional) : (asignatura.obligatoria !== undefined ? !asignatura.obligatoria : false);
        const newAsig = { ...asignatura, porcentaje_logro, concepto_final, en_papelera: Boolean(asignatura.en_papelera), es_opcional, obligatoria: !es_opcional, calificacion_oficial: asignatura.calificacion_oficial ?? null, aplazos: asignatura.aplazos ?? [] };
        preparadas.push(newAsig);

        batchStatements.push({
          query: `INSERT INTO asignaturas (id, plan_id, nombre, codigo, semestre_sugerido, creditos, departamento, facultad_origen, estado, requisitos, periodo_cursado, fecha_inicio, fecha_fin, fecha_aprobacion, ano_cursado, nota_final, porcentaje_logro, concepto_final, posicion_x, posicion_y, eva_url, sala9_url, en_papelera, es_opcional, dictado, previas_sugeridas, calificacion_oficial, aplazos, modulo_id, ciclo_id) 
           VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18, $19, $20, $21, $22, $23, $24, $25, $26, $27, $28, $29, $30)
           ON CONFLICT(id) DO UPDATE SET
             nombre = excluded.nombre,
             codigo = excluded.codigo,
             semestre_sugerido = excluded.semestre_sugerido,
             creditos = excluded.creditos,
             departamento = excluded.departamento,
             requisitos = excluded.requisitos,
             es_opcional = excluded.es_opcional,
             dictado = excluded.dictado,
             previas_sugeridas = excluded.previas_sugeridas,
             calificacion_oficial = excluded.calificacion_oficial,
             modulo_id = excluded.modulo_id,
             ciclo_id = excluded.ciclo_id`,
          params: [
            newAsig.id, newAsig.plan_id, newAsig.nombre, newAsig.codigo || '', 
            newAsig.semestre_sugerido || null, newAsig.creditos, newAsig.departamento || '', 
            newAsig.facultad_origen || '', newAsig.estado, 
            newAsig.requisitos ? JSON.stringify(newAsig.requisitos) : null,
            newAsig.periodo_cursado || null, newAsig.fecha_inicio || null, newAsig.fecha_fin || null,
            newAsig.fecha_aprobacion || null, newAsig.ano_cursado || null, newAsig.nota_final || null,
            newAsig.porcentaje_logro ?? null, newAsig.concepto_final || null,
            newAsig.posicion_x ?? null, newAsig.posicion_y ?? null,
            newAsig.eva_url || null, newAsig.sala9_url || null,
            newAsig.en_papelera ? 1 : 0,
            newAsig.es_opcional ? 1 : 0,
            newAsig.dictado || null,
            newAsig.previas_sugeridas ? JSON.stringify(newAsig.previas_sugeridas) : null,
            newAsig.calificacion_oficial,
            JSON.stringify(newAsig.aplazos),
            newAsig.modulo_id || null,
            newAsig.ciclo_id || null
          ]
        });
      }

      await db.executeBatch(batchStatements);

      set(state => {
        const map = new Map(state.asignaturas.map(a => [a.id, a]));
        preparadas.forEach(p => {
          const existente = map.get(p.id);
          if (existente) {
            map.set(p.id, {
              ...existente,
              nombre: p.nombre,
              codigo: p.codigo,
              semestre_sugerido: p.semestre_sugerido,
              creditos: p.creditos,
              departamento: p.departamento,
              requisitos: p.requisitos,
              es_opcional: p.es_opcional,
              obligatoria: p.obligatoria,
              dictado: p.dictado,
              previas_sugeridas: p.previas_sugeridas,
              calificacion_oficial: p.calificacion_oficial
            });
          } else {
            map.set(p.id, p);
          }
        });
        return { asignaturas: Array.from(map.values()) };
      });
    } catch (error) {
      console.error('Error adding/updating asignaturas batch:', error);
    }
  },

  updateAsignatura: async (id: string, updates: Partial<Asignatura>) => {
    try {
      const escalas = get().escalas;
      const escalaFCEA = escalas.find(e => e.facultad === 'FCEA') || DEFAULT_ESCALA_FCEA;
      
      let finalUpdates = { ...updates };
      const calif = resolverCalificacionItem(updates, escalaFCEA);
      if (calif) {
        if (updates.porcentaje_logro !== undefined) {
          finalUpdates.concepto_final = calif.concepto;
        } else if (updates.concepto_final !== undefined) {
          finalUpdates.porcentaje_logro = calif.porcentaje_min;
        }
      }

      if (get().isSimulating) {
        set(state => ({
          asignaturas: state.asignaturas.map(a => a.id === id ? { ...a, ...finalUpdates } : a),
          simulationChanges: state.simulationChanges + 1
        }));
        return;
      }

      const db = await getDb();

      if (finalUpdates.en_papelera !== undefined) {
        (finalUpdates as any).en_papelera = finalUpdates.en_papelera ? 1 : 0;
      }
      if (finalUpdates.es_opcional !== undefined) {
        (finalUpdates as any).es_opcional = finalUpdates.es_opcional ? 1 : 0;
      }

      const keys = Object.keys(finalUpdates);
      const values = Object.values(finalUpdates).map(v => {
        if (v === undefined) return null;
        return typeof v === 'object' ? JSON.stringify(v) : v;
      });
      
      const setClause = keys.map((key, i) => `${key} = $${i + 1}`).join(', ');
      
      await db.execute(
        `UPDATE asignaturas SET ${setClause} WHERE id = $${keys.length + 1}`,
        [...values, id]
      );

      set(state => ({
        asignaturas: state.asignaturas.map(a => a.id === id ? { ...a, ...updates } : a)
      }));
    } catch (error) {
      console.error('Error updating asignatura:', error);
    }
  },

  deleteAsignatura: async (id: string) => {
    // Soft delete -> Enviar a la Papelera con timestamp
    await get().updateAsignatura(id, { en_papelera: true, eliminado_en: new Date().toISOString() });
  },

  hardDeleteAsignatura: async (id: string) => {
    try {
      const db = await getDb();
      await db.execute(`DELETE FROM asignaturas WHERE id = $1`, [id]);
      set(state => ({
        asignaturas: state.asignaturas.filter(a => a.id !== id)
      }));
    } catch (error) {
      console.error('Error hard-deleting asignatura:', error);
    }
  },

  addEvaluacion: async (evaluacion: Evaluacion) => {
    try {
      const db = await getDb();
      const escalas = get().escalas;
      const escalaFCEA = escalas.find(e => e.facultad === 'FCEA') || DEFAULT_ESCALA_FCEA;

      let pct = evaluacion.porcentaje_logro;
      let concepto = evaluacion.concepto;

      if (pct !== undefined && !concepto) {
        concepto = resolverConcepto(pct, escalaFCEA).concepto;
      } else if (concepto && pct === undefined) {
        const niv = escalaFCEA.niveles.find(n => n.concepto.toLowerCase() === concepto!.toLowerCase());
        if (niv) pct = niv.porcentaje_min;
      } else if (evaluacion.nota !== undefined && pct === undefined) {
        pct = (evaluacion.nota / 12) * 100;
        concepto = resolverConcepto(pct, escalaFCEA).concepto;
      }

      const newEval = { ...evaluacion, porcentaje_logro: pct, concepto };

      await db.execute(
        `INSERT OR REPLACE INTO evaluaciones (id, asignatura_id, tipo, nombre, fecha, porcentaje_logro, concepto, nota, escala) 
         VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9)`,
        [
          newEval.id, newEval.asignatura_id, newEval.tipo, newEval.nombre,
          newEval.fecha || null, newEval.porcentaje_logro ?? null, newEval.concepto || null,
          newEval.nota ?? null, newEval.escala || null
        ]
      );
      set(state => ({ evaluaciones: [...state.evaluaciones, newEval] }));
    } catch (error) {
      console.error('Error adding evaluacion:', error);
    }
  },


  updateEvaluacion: async (id: string, updates: Partial<Evaluacion>) => {
    try {
      const db = await getDb();
      const escalas = get().escalas;
      const escalaFCEA = escalas.find(e => e.facultad === 'FCEA') || DEFAULT_ESCALA_FCEA;

      let finalUpdates = { ...updates };
      const calif = resolverCalificacionItem(updates, escalaFCEA);
      if (calif) {
        if (updates.porcentaje_logro !== undefined) {
          finalUpdates.concepto = calif.concepto;
        } else if (updates.concepto !== undefined) {
          finalUpdates.porcentaje_logro = calif.porcentaje_min;
        }
      }

      const keys = Object.keys(finalUpdates);
      const values = Object.values(finalUpdates).map(v => {
        if (v === undefined) return null;
        return typeof v === 'object' ? JSON.stringify(v) : v;
      });
      const setClause = keys.map((key, i) => `${key} = $${i + 1}`).join(', ');

      await db.execute(
        `UPDATE evaluaciones SET ${setClause} WHERE id = $${keys.length + 1}`,
        [...values, id]
      );

      set(state => ({
        evaluaciones: state.evaluaciones.map(e => e.id === id ? { ...e, ...finalUpdates } : e)
      }));
    } catch (error) {
      console.error('Error updating evaluacion:', error);
    }
  },

  deleteEvaluacion: async (id: string) => {
    try {
      const db = await getDb();
      await db.execute(`DELETE FROM evaluaciones WHERE id = $1`, [id]);
      set(state => ({ evaluaciones: state.evaluaciones.filter(e => e.id !== id) }));
    } catch (error) {
      console.error('Error deleting evaluacion:', error);
    }
  },

  updateEscala: async (escala: EscalaCalificacion) => {

    try {
      const db = await getDb();
      await db.execute(
        `UPDATE escalas_calificacion SET nombre = $1, niveles = $2 WHERE id = $3`,
        [escala.nombre, JSON.stringify(escala.niveles), escala.id]
      );
      set(state => ({
        escalas: state.escalas.map(e => e.id === escala.id ? escala : e)
      }));
    } catch (error) {
      console.error('Error updating escala:', error);
    }
  },

  addMaterial: async (material: Material) => {
    try {
      const db = await getDb();
      await db.execute(
        `INSERT OR REPLACE INTO materiales (id, asignatura_id, tipo, ruta_relativa, nombre, tags, fecha_agregado, url_r2) 
         VALUES ($1, $2, $3, $4, $5, $6, $7, $8)`,
        [
          material.id, material.asignatura_id, material.tipo, material.ruta_relativa,
          material.nombre, material.tags || null, material.fecha_agregado, material.url_r2 || null
        ]
      );
      set(state => ({ materiales: [...state.materiales, material] }));
    } catch (error) {
      console.error('Error adding material:', error);
    }
  },

  deleteMaterial: async (id: string) => {
    try {
      const db = await getDb();
      await db.execute(`DELETE FROM materiales WHERE id = $1`, [id]);
      set(state => ({ materiales: state.materiales.filter(m => m.id !== id) }));
    } catch (error) {
      console.error('Error deleting material:', error);
    }
  },

  addCarpetaNota: async (carpeta: CarpetaNota) => {
    try {
      const db = await getDb();
      await db.execute(
        `INSERT OR REPLACE INTO carpetas_notas (id, nombre, padre_id, fecha_creacion) VALUES ($1, $2, $3, $4)`,
        [carpeta.id, carpeta.nombre, carpeta.padre_id || null, carpeta.fecha_creacion]
      );
      set(state => ({ carpetasNotas: [...state.carpetasNotas, carpeta] }));
    } catch (error) {
      console.error('Error adding carpeta nota:', error);
    }
  },

  updateCarpetaNota: async (id: string, updates: Partial<CarpetaNota>) => {
    try {
      const db = await getDb();
      const keys = Object.keys(updates);
      const values = Object.values(updates);
      const setClause = keys.map((key, i) => `${key} = $${i + 1}`).join(', ');
      await db.execute(
        `UPDATE carpetas_notas SET ${setClause} WHERE id = $${keys.length + 1}`,
        [...values, id]
      );
      set(state => ({
        carpetasNotas: state.carpetasNotas.map(c => c.id === id ? { ...c, ...updates } : c)
      }));
    } catch (error) {
      console.error('Error updating carpeta nota:', error);
    }
  },

  deleteCarpetaNota: async (id: string) => {
    try {
      const db = await getDb();
      await db.execute(`DELETE FROM carpetas_notas WHERE id = $1`, [id]);
      set(state => ({
        carpetasNotas: state.carpetasNotas.filter(c => c.id !== id),
        // Pasar las notas de esa carpeta a la raíz
        notasGenerales: state.notasGenerales.map(n => n.carpeta_id === id ? { ...n, carpeta_id: undefined } : n)
      }));
    } catch (error) {
      console.error('Error deleting carpeta nota:', error);
    }
  },

  addNota: async (nota: NotaGeneral) => {
    try {
      const db = await getDb();
      const newNota = { ...nota, en_papelera: Boolean(nota.en_papelera) };
      await db.execute(
        `INSERT OR REPLACE INTO notas_generales (id, titulo, contenido, carpeta_id, asignatura_id, evento_id, tags, en_papelera, fecha_creacion, fecha_modificacion)
         VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)`,
        [
          newNota.id, newNota.titulo, newNota.contenido, newNota.carpeta_id || null,
          newNota.asignatura_id || null, newNota.evento_id || null, newNota.tags ? JSON.stringify(newNota.tags) : null,
          newNota.en_papelera ? 1 : 0, newNota.fecha_creacion, newNota.fecha_modificacion
        ]
      );
      set(state => ({ notasGenerales: [newNota, ...state.notasGenerales] }));
    } catch (error) {
      console.error('Error adding nota:', error);
    }
  },

  updateNota: async (id: string, updates: Partial<NotaGeneral>) => {
    try {
      const db = await getDb();
      let finalUpdates = { ...updates, fecha_modificacion: new Date().toISOString() };
      
      if (finalUpdates.en_papelera !== undefined) {
        (finalUpdates as any).en_papelera = finalUpdates.en_papelera ? 1 : 0;
      }

      const keys = Object.keys(finalUpdates);
      const values = Object.values(finalUpdates).map(v => {
        if (v === undefined) return null;
        return typeof v === 'object' ? JSON.stringify(v) : v;
      });
      const setClause = keys.map((key, i) => `${key} = $${i + 1}`).join(', ');

      await db.execute(
        `UPDATE notas_generales SET ${setClause} WHERE id = $${keys.length + 1}`,
        [...values, id]
      );


      set(state => ({
        notasGenerales: state.notasGenerales.map(n => n.id === id ? { ...n, ...updates, fecha_modificacion: finalUpdates.fecha_modificacion } : n)
      }));
    } catch (error) {
      console.error('Error updating nota:', error);
    }
  },

  deleteNota: async (id: string) => {
    // Soft delete -> Mover a la papelera con timestamp
    await get().updateNota(id, { en_papelera: true, eliminado_en: new Date().toISOString() });
  },

  hardDeleteNota: async (id: string) => {
    try {
      const db = await getDb();
      await db.execute(`DELETE FROM notas_generales WHERE id = $1`, [id]);
      set(state => ({
        notasGenerales: state.notasGenerales.filter(n => n.id !== id)
      }));
    } catch (error) {
      console.error('Error hard-deleting nota:', error);
    }
  },

  addHorario: async (horario: HorarioClase) => {
    try {
      const db = await getDb();
      await db.execute(
        `INSERT OR REPLACE INTO horarios_clases (id, asignatura_id, dia, hora_inicio, hora_fin, grupo, salon, docente, observaciones, periodo)
         VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)`,
        [
          horario.id, horario.asignatura_id, horario.dia, horario.hora_inicio, horario.hora_fin,
          horario.grupo || null, horario.salon || null, horario.docente || null, horario.observaciones || null,
          horario.periodo || null
        ]
      );
      set(state => ({ horarios: [...state.horarios, horario] }));
    } catch (error) {
      console.error('Error adding horario:', error);
    }
  },

  updateHorario: async (id: string, updates: Partial<HorarioClase>) => {
    try {
      const db = await getDb();
      const keys = Object.keys(updates);
      if (keys.length === 0) return;

      const values = Object.values(updates).map(v => v === undefined ? null : v);
      const setClause = keys.map((key, i) => `${key} = $${i + 1}`).join(', ');

      await db.execute(
        `UPDATE horarios_clases SET ${setClause} WHERE id = $${keys.length + 1}`,
        [...values, id]
      );

      set(state => ({
        horarios: state.horarios.map(h => h.id === id ? { ...h, ...updates } : h)
      }));
    } catch (error) {
      console.error('Error updating horario:', error);
    }
  },

  deleteHorario: async (id: string) => {
    try {
      const db = await getDb();
      await db.execute(`DELETE FROM horarios_clases WHERE id = $1`, [id]);
      set(state => ({
        horarios: state.horarios.filter(h => h.id !== id)
      }));
    } catch (error) {
      console.error('Error deleting horario:', error);
    }
  },

  addEventoCalendario: async (evento: EventoCalendario) => {
    try {
      const db = await getDb();
      await db.execute(
        `INSERT OR REPLACE INTO eventos_calendario (id, tipo, asignatura_id, fecha, descripcion, hora)
         VALUES ($1, $2, $3, $4, $5, $6)`,
        [
          evento.id, evento.tipo, evento.asignatura_id || null,
          evento.fecha, evento.descripcion, evento.hora || null
        ]
      );
      set(state => ({
        eventosCalendario: [...state.eventosCalendario, evento].sort((a, b) => a.fecha.localeCompare(b.fecha))
      }));
    } catch (error) {
      console.error('Error adding evento calendario:', error);
    }
  },

  updateEventoCalendario: async (id: string, updates: Partial<EventoCalendario>) => {
    try {
      const db = await getDb();
      const keys = Object.keys(updates);
      if (keys.length === 0) return;

      const values = Object.values(updates).map(v => v === undefined ? null : v);
      const setClause = keys.map((key, i) => `${key} = $${i + 1}`).join(', ');

      await db.execute(
        `UPDATE eventos_calendario SET ${setClause} WHERE id = $${keys.length + 1}`,
        [...values, id]
      );

      set(state => ({
        eventosCalendario: state.eventosCalendario
          .map(e => e.id === id ? { ...e, ...updates } : e)
          .sort((a, b) => a.fecha.localeCompare(b.fecha))
      }));
    } catch (error) {
      console.error('Error updating evento calendario:', error);
    }
  },

  deleteEventoCalendario: async (id: string) => {
    try {
      const db = await getDb();
      await db.execute(`DELETE FROM eventos_calendario WHERE id = $1`, [id]);
      set(state => ({
        eventosCalendario: state.eventosCalendario.filter(e => e.id !== id)
      }));
    } catch (error) {
      console.error('Error deleting evento calendario:', error);
    }
  },

  addRegistroHistorial: async (registro: RegistroHistorial) => {
    try {
      const db = await getDb();
      await db.execute(
        `INSERT OR REPLACE INTO historial_academico (id, asignatura_id, fecha, instancia, resultado, calificacion)
         VALUES ($1, $2, $3, $4, $5, $6)`,
        [
          registro.id, registro.asignatura_id, registro.fecha,
          registro.instancia, registro.resultado, registro.calificacion
        ]
      );
      const newHistorial = [...get().historialAcademico, registro];
      set({ historialAcademico: newHistorial });

      // Auto-sincronizar el estado de la asignatura
      if (registro.resultado === 'aprobado') {
        await get().updateAsignatura(registro.asignatura_id, { estado: 'aprobada' });
      } else if (registro.resultado === 'reprobado') {
        const tieneAprobacion = newHistorial.some(r => r.asignatura_id === registro.asignatura_id && r.resultado === 'aprobado');
        if (!tieneAprobacion) {
          await get().updateAsignatura(registro.asignatura_id, { estado: 'a_examen' });
        }
      }
    } catch (error) {
      console.error('Error adding registro historial:', error);
    }
  },

  updateRegistroHistorial: async (id: string, updates: Partial<RegistroHistorial>) => {
    try {
      const db = await getDb();
      const keys = Object.keys(updates);
      if (keys.length === 0) return;

      const values = Object.values(updates).map(v => v === undefined ? null : v);
      const setClause = keys.map((key, i) => `${key} = $${i + 1}`).join(', ');

      await db.execute(
        `UPDATE historial_academico SET ${setClause} WHERE id = $${keys.length + 1}`,
        [...values, id]
      );

      const newHistorial = get().historialAcademico.map(r => r.id === id ? { ...r, ...updates } : r);
      set({ historialAcademico: newHistorial });

      const regTarget = newHistorial.find(r => r.id === id);
      if (regTarget) {
        if (regTarget.resultado === 'aprobado') {
          await get().updateAsignatura(regTarget.asignatura_id, { estado: 'aprobada' });
        } else if (regTarget.resultado === 'reprobado') {
          const tieneAprobacion = newHistorial.some(r => r.asignatura_id === regTarget.asignatura_id && r.resultado === 'aprobado');
          if (!tieneAprobacion) {
            await get().updateAsignatura(regTarget.asignatura_id, { estado: 'a_examen' });
          }
        }
      }
    } catch (error) {
      console.error('Error updating registro historial:', error);
    }
  },

  deleteRegistroHistorial: async (id: string) => {
    try {
      const regTarget = get().historialAcademico.find(r => r.id === id);
      const db = await getDb();
      await db.execute(`DELETE FROM historial_academico WHERE id = $1`, [id]);
      const newHistorial = get().historialAcademico.filter(r => r.id !== id);
      set({ historialAcademico: newHistorial });

      if (regTarget) {
        const registrosRestantes = newHistorial.filter(r => r.asignatura_id === regTarget.asignatura_id);
        if (registrosRestantes.some(r => r.resultado === 'aprobado')) {
          await get().updateAsignatura(regTarget.asignatura_id, { estado: 'aprobada' });
        } else if (registrosRestantes.length > 0) {
          await get().updateAsignatura(regTarget.asignatura_id, { estado: 'a_examen' });
        }
      }
    } catch (error) {
      console.error('Error deleting registro historial:', error);
    }
  }
}));

/**
 * Selectores centralizados de Soft-Delete (Regla: soft-delete-single-source)
 * Garantiza que todo acceso a entidades activas pase por un único lugar.
 */
export function selectAsignaturasActivas(asignaturas: Asignatura[]): Asignatura[] {
  return asignaturas.filter(a => !a.en_papelera);
}

export function selectNotasActivas(notas: NotaGeneral[]): NotaGeneral[] {
  return notas.filter(n => !n.en_papelera);
}





